import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "@/components/feature/EmptyState";
import { newsList, newsCategories } from "@/mocks/news";

const PAGE_SIZE = 5;

export default function News() {
  const [category, setCategory] = useState("全部");
  const [range, setRange] = useState("全部时间");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const timeRanges = ["全部时间", "近一个月", "近三个月", "更早"];

  const filtered = useMemo(() => {
    let list = newsList.filter((n) => category === "全部" || n.category === category);
    const latest = new Date("2026-09-30").getTime();
    list = list.filter((n) => {
      const diffDays = (latest - new Date(n.date).getTime()) / 86400000;
      if (range === "近一个月") return diffDays <= 31;
      if (range === "近三个月") return diffDays <= 93;
      if (range === "更早") return diffDays > 93;
      return true;
    });
    return list.sort((a, b) => b.date.localeCompare(a.date));
  }, [category, range]);

  const shown = filtered.slice(0, visible);

  const change = (c: string) => {
    setCategory(c);
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="w-full">
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-xs text-secondary-500 mb-3">
            <Link to="/" className="hover:text-accent-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">最新资讯</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950">最新资讯</h1>
          <p className="mt-3 text-sm md:text-base text-secondary-500 max-w-2xl">
            汇聚影视动态、幕后花絮与深度观察，带你了解经典作品背后的故事。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {newsCategories.map((c) => (
              <button
                key={c}
                onClick={() => change(c)}
                className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  category === c
                    ? "bg-primary-500 text-foreground-950"
                    : "bg-background-100 text-foreground-700 border border-background-200 hover:border-accent-500"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-secondary-500 whitespace-nowrap">时间筛选</span>
            <div className="flex p-1 rounded-full bg-background-100 border border-background-200">
              {timeRanges.map((t) => (
                <button
                  key={t}
                  onClick={() => {
                    setRange(t);
                    setVisible(PAGE_SIZE);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    range === t ? "bg-accent-500 text-primary-950" : "text-secondary-500 hover:text-foreground-900"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {shown.length > 0 ? (
          <>
            <div className="mt-8 space-y-4">
              {shown.map((news) => (
                <article
                  key={news.id}
                  className="group flex flex-col sm:flex-row gap-4 md:gap-6 rounded-lg bg-background-100 border border-background-200 hover:border-primary-500 p-3 md:p-4 transition-colors"
                >
                  <div className="w-full sm:w-56 shrink-0 aspect-video rounded-md overflow-hidden bg-background-200">
                    <img
                      src={news.cover}
                      alt={news.title}
                      title={`${news.title} - 影视资讯`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-xs text-secondary-500">
                      <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-900">{news.category}</span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        {news.date}
                      </span>
                    </div>
                    <h2 className="mt-2.5 font-heading text-base md:text-xl font-semibold text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-2">
                      {news.title}
                    </h2>
                    <p className="mt-2 text-xs md:text-sm text-secondary-500 line-clamp-2 md:line-clamp-3 leading-relaxed">
                      {news.excerpt}
                    </p>
                    <div className="mt-auto pt-3 flex items-center justify-between text-xs text-secondary-500">
                      <span>来源：{news.source}</span>
                      <span className="inline-flex items-center gap-1 group-hover:text-accent-500 transition-colors">
                        阅读全文
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {visible < filtered.length && (
              <div className="mt-10 flex justify-center">
                <button
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-100 border border-background-200 text-foreground-800 text-sm hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  加载更多
                </button>
              </div>
            )}
          </>
        ) : (
          <EmptyState icon="ri-newspaper-line" title="暂无相关资讯" description="换个分类或时间范围看看吧。" />
        )}
      </div>
    </div>
  );
}